import { taskManagerApiPaths } from '@/hooks/api/_paths'
import { useApiRequest } from '@/lib/http-utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { APIErrorResponse } from './_types'
import { DataAssetType } from './use-data-assets-query'

const DELETE_DATA_ASSET_ERROR = 'FAILED_TO_DELETE_DATA_ASSET'

export type DeleteDataAssetsInput = {
  deleteRequests: Array<{
    dataAssetFqdn: string
    dataAssetType: DataAssetType
  }>
}

type DeleteResponse = {
  successful_deletions: string[]
  failed_deletions: Array<{
    fqdn: string
    error: string
  }>
  total_requested: number
  total_successful: number
  total_failed: number
}

function useDeleteDataAssetsMutation() {
  const { apiRequest, domainId } = useApiRequest()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['delete-data-assets', domainId],
    mutationFn: async (data: DeleteDataAssetsInput) => {
      const response = await apiRequest({
        path: taskManagerApiPaths.deleteDataAssets({ domainId }),
        options: {
          method: 'POST',
          body: JSON.stringify(data),
        },
      })

      if (!response.ok) {
        const errorData = (await response.json()) as APIErrorResponse
        if (errorData?.detail) {
          throw new Error(errorData.detail)
        }
        throw new Error(DELETE_DATA_ASSET_ERROR)
      }

      // Parse the response body to check for failed deletions
      const deleteResult = (await response.json()) as DeleteResponse

      // If there are any failed deletions, throw an error
      if (deleteResult.total_failed && deleteResult.total_failed > 0) {
        throw new Error(DELETE_DATA_ASSET_ERROR)
      }

      return deleteResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['data-assets', domainId],
      })
    },
  })
}

export { DELETE_DATA_ASSET_ERROR, useDeleteDataAssetsMutation }
